import { enqueueSnackbar } from 'notistack';

import { LoadingButton } from '@mui/lab';

import { useTranslate } from 'src/locales';
import { CallListPersonType } from 'src/services/call-lists/call-lists.types';
import { useSetDoNotCallMutation } from 'src/services/call-lists/call-lists.service';

import { ConfirmDialog } from 'src/components/custom-dialog';

// ----------------------------------------------------------------------

type Props = {
  open: boolean;
  onClose: VoidFunction;
  personType: CallListPersonType;
  personId: string;
  personName?: string;
  /** The value to set: true marks the person do not call, false clears it. */
  doNotCall: boolean;
};

/** Confirms marking someone do not call (or clearing it) before it applies across the agency. */
export default function DoNotCallDialog({ open, onClose, personType, personId, personName, doNotCall }: Props) {
  const { t } = useTranslate();
  const [setDoNotCall, { isLoading }] = useSetDoNotCallMutation();

  const confirm = async () => {
    try {
      await setDoNotCall({ entityType: personType, entityId: personId, doNotCall }).unwrap();
      enqueueSnackbar(t(doNotCall ? 'call_lists.dnc.marked' : 'call_lists.dnc.cleared'), { variant: 'success' });
      onClose();
    } catch (error: any) {
      enqueueSnackbar(error?.data?.message || t('call_lists.toasts.action_failed'), { variant: 'error' });
    }
  };

  return (
    <ConfirmDialog
      open={open}
      onClose={onClose}
      title={t(doNotCall ? 'call_lists.dnc.mark_title' : 'call_lists.dnc.clear_title', { name: personName || '' })}
      content={t(doNotCall ? 'call_lists.dnc.mark_content' : 'call_lists.dnc.clear_content')}
      action={(
        <LoadingButton variant="contained" color={doNotCall ? 'error' : 'primary'} loading={isLoading} onClick={confirm} data-testid="do-not-call-confirm">
          {t(doNotCall ? 'call_lists.dnc.mark' : 'call_lists.dnc.clear')}
        </LoadingButton>
      )}
    />
  );
}
